import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import type { Machine } from '../type/Machine';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, Plus, Save, Trash2, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { InlineConfirm } from '@/components/shared/InlineConfirm';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { useTranslation } from '@/hooks/useTranslation';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { useSignalSources } from '@/features/signals/hooks/useSignalMachines';
import { SignalSourceSelect } from './SignalSourceSelect';

type MachineManagerPanelProps = {
  companyName: string;
  traceabilityApiClient: TraceabilityApiClient | null;
  onSave?: (machine?: Machine, action?: 'add' | 'update' | 'delete') => void;
  selectedMachineName?: string | null;
};

export const MachineManagerPanel: React.FC<MachineManagerPanelProps> = ({
  companyName,
  traceabilityApiClient,
  onSave,
  selectedMachineName: initialSelectedName,
}) => {
  const DEFAULT_MACHINE: Omit<Machine, 'id' | 'companyName'> & { id?: number } = useMemo(
    () => ({
      name: '',
      brand: '',
      model: '',
      serialNumber: '',
      kafkaName: '',
    }),
    []
  );

  const [machines, setMachines] = useState<Machine[]>([]);
  const [formMachine, setFormMachine] = useState<typeof DEFAULT_MACHINE>(DEFAULT_MACHINE);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const signalSources = useSignalSources();
  // A signal source belongs to one machine: the ones other machines use are refused.
  const linkedBy = useMemo(
    () =>
      new Map(
        machines
          .filter((machine) => machine.kafkaName && machine.id !== formMachine.id)
          .map((machine) => [machine.kafkaName as string, machine.name])
      ),
    [machines, formMachine.id]
  );
  const { t } = useTranslation();
  const writeControllerRef = useRef<AbortController | null>(null);

  // Abort any in-flight write/delete on unmount
  useEffect(() => {
    return () => {
      writeControllerRef.current?.abort();
    };
  }, []);

  // Reset deleteConfirm when selection changes
  useEffect(() => {
    setDeleteConfirm(false);
  }, [selectedId]);

  // Load machines when traceabilityApiClient becomes ready
  useEffect(() => {
    if (!traceabilityApiClient) return;
    const ac = new AbortController();
    setIsLoading(true);
    setFormError(null);

    traceabilityApiClient
      .machineReadAll({}, { signal: ac.signal })
      .then((response) => {
        if (ac.signal.aborted) return;
        if (response.ok && response.data?.machines) {
          setMachines(response.data.machines.map((item: { machine: Machine }) => item.machine));
        } else {
          setFormError(
            response.ok
              ? t('traceability.machineManager.errors.noData')
              : response.message || t('traceability.machineManager.errors.fetchFailed')
          );
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.error('Failed to fetch machines:', err);
        setFormError(t('traceability.machineManager.errors.fetchFailed'));
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoading(false);
      });

    // selectedId will be resolved once machines load (see effect below)

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceabilityApiClient]);

  // Update form when machines are loaded and selectedMachineName is set
  useEffect(() => {
    if (initialSelectedName && machines.length > 0) {
      const machine = machines.find((m) => m.name === initialSelectedName);
      if (machine) {
        setFormMachine(machine);
        setSelectedId(machine.id);
      }
    }
  }, [machines, initialSelectedName]);

  const refreshMachines = async (machine?: Machine, action?: 'add' | 'update' | 'delete') => {
    if (!traceabilityApiClient) {
      onSave?.(machine, action);
      return;
    }

    setIsLoading(true);
    setFormError(null);

    const currentAc = writeControllerRef.current;

    try {
      const response = await traceabilityApiClient.machineReadAll({});
      if (currentAc?.signal.aborted) return;
      if (response.ok && response.data?.machines) {
        setMachines(response.data.machines.map((item: { machine: Machine }) => item.machine));
      }
    } catch (err: unknown) {
      console.error('Failed to refresh machines:', err);
    } finally {
      if (!currentAc?.signal.aborted) setIsLoading(false);
    }

    onSave?.(machine, action);
  };

  const handleAddOrUpdate = async () => {
    if (!traceabilityApiClient) {
      setFormError(t('traceability.machineManager.errors.apiNotReady'));
      return;
    }

    const trimmedName = formMachine.name.trim();

    if (!trimmedName) {
      setFormError(t('traceability.machineManager.errors.nameRequired'));
      return;
    }

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;

    setIsSaving(true);
    setFormError(null);

    try {
      const payload: Machine = {
        ...(formMachine.id !== undefined && { id: formMachine.id }),
        name: trimmedName,
        brand: formMachine.brand.trim(),
        model: formMachine.model.trim(),
        serialNumber: formMachine.serialNumber.trim(),
        companyName: companyName,
        ...(formMachine.kafkaName?.trim() && { kafkaName: formMachine.kafkaName.trim() }),
      };

      const response = await traceabilityApiClient.machineWrite(
        { machine: payload },
        { signal: ac.signal }
      );

      if (ac.signal.aborted) return;

      if (response.ok) {
        const savedPayload = { ...payload, id: response.data.id };
        await refreshMachines(savedPayload, formMachine.id !== undefined ? 'update' : 'add');
        if (ac.signal.aborted) return;
        setFormMachine(savedPayload);
        setSelectedId(response.data.id);
      } else {
        setFormError(response.message || t('traceability.machineManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to save machine:', error);
      setFormError(t('traceability.machineManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    if (!traceabilityApiClient) {
      setFormError(t('traceability.machineManager.errors.apiNotReady'));
      return;
    }

    if (selectedId === undefined) {
      setFormError(t('traceability.machineManager.errors.selectBeforeDelete'));
      return;
    }

    const machineToDelete = machines.find((machine) => machine.id === selectedId);
    if (!machineToDelete) {
      setFormError(t('traceability.machineManager.errors.notFound'));
      return;
    }
    if (machineToDelete.id === undefined) {
      setFormError(t('traceability.machineManager.errors.notFound'));
      return;
    }

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;

    setIsDeleting(true);
    setFormError(null);

    try {
      const response = await traceabilityApiClient.machineDelete(
        { id: machineToDelete.id },
        { signal: ac.signal }
      );

      if (ac.signal.aborted) return;

      if (response.ok) {
        await refreshMachines(machineToDelete, 'delete');
        if (ac.signal.aborted) return;
        setSelectedId(undefined);
        setFormMachine(DEFAULT_MACHINE);
        setDeleteConfirm(false);
      } else {
        setFormError(response.message || t('traceability.machineManager.errors.deleteFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to delete machine:', error);
      setFormError(t('traceability.machineManager.errors.deleteFailed'));
    } finally {
      if (!ac.signal.aborted) setIsDeleting(false);
    }
  };

  const handleSelectMachine = (machine: Machine) => {
    setFormError(null);
    setFormMachine(machine);
    setSelectedId(machine.id);
  };

  const handleNewMachine = () => {
    setFormError(null);
    setFormMachine(DEFAULT_MACHINE);
    setSelectedId(undefined);
  };

  const isEditing = formMachine.id !== undefined;

  return (
    <div className="flex flex-col gap-4">
      {formError && <Callout tone="error">{formError}</Callout>}

      {/* Mobile machine selector — visible only below md */}
      <div className="mb-4 md:hidden">
        <SimpleSelect
          aria-label={t('traceability.machineManager.list.title')}
          value={selectedId !== undefined ? String(selectedId) : null}
          onChange={(next) => {
            const id = next ? Number(next) : undefined;
            const m = id !== undefined ? machines.find((m) => m.id === id) : undefined;
            if (m) handleSelectMachine(m);
            else handleNewMachine();
          }}
          disabled={isLoading || isSaving || isDeleting}
          noneLabel={t('traceability.machineManager.form.new')}
          options={machines.map((m) => ({
            value: m.id !== undefined ? String(m.id) : m.name,
            label: `${m.name} — ${m.brand} ${m.model}`,
          }))}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <Card>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <SectionLabel icon={Wrench}>
              {t('traceability.machineManager.form.sectionTitle')}
            </SectionLabel>
            {selectedId !== undefined && formMachine.name && (
              <Badge tone="primary" caps>
                {formMachine.name}
              </Badge>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <SimpleInput
              name="machine-name"
              label={t('traceability.machineManager.form.fields.name.label')}
              value={formMachine.name}
              onChange={(val) => setFormMachine((prev) => ({ ...prev, name: val as string }))}
              placeholder={t('traceability.machineManager.form.fields.name.placeholder')}
              required
              className="sm:col-span-2"
            />
            <SimpleInput
              name="machine-brand"
              label={t('traceability.machineManager.form.fields.brand.label')}
              value={formMachine.brand}
              onChange={(val) => setFormMachine((prev) => ({ ...prev, brand: val as string }))}
              placeholder={t('traceability.machineManager.form.fields.brand.placeholder')}
              required
            />
            <SimpleInput
              name="machine-model"
              label={t('traceability.machineManager.form.fields.model.label')}
              value={formMachine.model}
              onChange={(val) => setFormMachine((prev) => ({ ...prev, model: val as string }))}
              placeholder={t('traceability.machineManager.form.fields.model.placeholder')}
              required
            />
            <SimpleInput
              name="machine-serial"
              label={t('traceability.machineManager.form.fields.serial.label')}
              value={formMachine.serialNumber}
              onChange={(val) =>
                setFormMachine((prev) => ({ ...prev, serialNumber: val as string }))
              }
              placeholder={t('traceability.machineManager.form.fields.serial.placeholder')}
              required
              className="sm:col-span-2"
            />
            <div className="sm:col-span-2">
              <SignalSourceSelect
                value={formMachine.kafkaName || null}
                onChange={(source) =>
                  setFormMachine((prev) => ({ ...prev, kafkaName: source ?? '' }))
                }
                sources={signalSources.data}
                isLoading={signalSources.isLoading}
                error={signalSources.error}
                onRetry={signalSources.retry}
                linkedBy={linkedBy}
                disabled={isSaving || isDeleting}
              />
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              className="flex-1"
              onClick={handleAddOrUpdate}
              loading={isSaving}
              disabled={isLoading}
            >
              {isSaving ? (
                t('traceability.machineManager.form.submit.saving')
              ) : (
                <>
                  <Save aria-hidden />
                  {isEditing
                    ? t('traceability.machineManager.form.submit.update')
                    : t('traceability.machineManager.form.submit.save')}
                </>
              )}
            </Button>
            <Button
              className="flex-1"
              variant="outline"
              onClick={handleNewMachine}
              disabled={isSaving || isLoading}
            >
              <Plus aria-hidden />
              {t('traceability.machineManager.form.new')}
            </Button>
          </div>
        </Card>

        <Card padding="sm" className="hidden md:flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-foreground">
              {t('traceability.machineManager.list.title')}
            </p>
            <span className="text-xs text-muted-foreground">
              {t('traceability.machineManager.list.count', { count: machines.length })}
            </span>
          </div>
          <div className="max-h-72 space-y-2 overflow-auto pr-1">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" className="text-primary" />
              </div>
            )}

            {!isLoading && machines.length === 0 && (
              <EmptyState size="compact" title={t('traceability.machineManager.list.empty')} />
            )}

            {!isLoading &&
              machines.map((machine) => (
                <button
                  key={machine.id ?? machine.name}
                  type="button"
                  onClick={() => handleSelectMachine(machine)}
                  disabled={isSaving || isDeleting}
                  className={cn(
                    'w-full rounded-xl border px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50',
                    machine.id === selectedId
                      ? 'border-primary/60 bg-primary/10 text-primary'
                      : 'border-border bg-card hover:border-primary/40 hover:bg-primary/5'
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold">{machine.name}</p>
                    {machine.kafkaName && (
                      <Badge
                        tone="primary"
                        size="sm"
                        className="font-mono"
                        title={t('traceability.machineManager.list.sourceBadgeTitle')}
                      >
                        <Check className="h-2.5 w-2.5" aria-hidden />
                        {machine.kafkaName}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {machine.brand} • {machine.model}
                  </p>
                  <p className="text-xs text-muted-foreground/80">
                    {t('traceability.machineManager.list.serial', {
                      serial: machine.serialNumber,
                    })}
                  </p>
                </button>
              ))}
          </div>

          {!deleteConfirm ? (
            <Button
              variant="destructive"
              className="mt-auto w-full"
              loading={isDeleting}
              disabled={selectedId === undefined || isSaving}
              onClick={() => setDeleteConfirm(true)}
            >
              {isDeleting ? (
                t('traceability.machineManager.delete.deleting')
              ) : (
                <>
                  <Trash2 aria-hidden />
                  {t('traceability.machineManager.delete.button')}
                </>
              )}
            </Button>
          ) : (
            <InlineConfirm
              className="mt-auto"
              confirmLabel={t('traceability.machineManager.confirmDelete')}
              cancelLabel={t('common.cancel')}
              busy={isDeleting}
              onConfirm={handleDelete}
              onCancel={() => setDeleteConfirm(false)}
            />
          )}
        </Card>
      </div>
    </div>
  );
};
